import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';
import qrcode from 'qrcode-generator';

/**
 * Renders a string as a QR code, as inline SVG.
 *
 * ### Why SVG and not a canvas
 *
 * The one job of this control is to be *readable by a camera held over a desk*. An SVG is resolution
 * independent, so the same element is crisp on a 4K monitor and on a scaled-down laptop panel, and it prints —
 * a canvas is a bitmap sized for whatever the browser happened to lay out. It also carries the accessible name
 * itself (`role="img"` + `aria-label`), where a canvas needs fallback content nobody remembers to write.
 *
 * ### Why one `<path>` and not a grid of `<rect>`s
 *
 * A version-3 code is 29×29 modules — up to 841 elements if each dark module is its own rectangle. They are
 * emitted as a single path of `M x y h1 v1 h-1 z` sub-paths instead: identical pixels, one DOM node, and no
 * hairline seams between neighbouring rectangles at fractional zoom levels (the artefact that makes a
 * rect-grid QR fail to scan on a scaled display).
 *
 * ### The quiet zone is part of the code
 *
 * The QR specification requires a 4-module light margin. Without it a scanner cannot find the finder patterns
 * against whatever the code is sitting on, so it is built into the `viewBox` rather than left to the caller's
 * padding — a caller who styles it away has broken the code and nothing says so.
 */
@Component({
  selector: 'cqme-qr-code',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg [attr.viewBox]="'0 0 ' + extent() + ' ' + extent()"
         [attr.width]="size()" [attr.height]="size()"
         [attr.aria-label]="ariaLabel()"
         role="img" shape-rendering="crispEdges" class="block h-auto max-w-full">
      <rect [attr.width]="extent()" [attr.height]="extent()" x="0" y="0" fill="#ffffff"></rect>
      <path [attr.d]="path()" fill="#111827"></path>
    </svg>
  `
})
export class QrCodeComponent {
  /** The exact text encoded into the code. */
  readonly value = input.required<string>();

  /** Rendered edge length in CSS pixels. The SVG scales, so this is a hint, not a resolution. */
  readonly size = input<number>(232);

  /**
   * The accessible name. A QR carries no text a screen reader can reach, so without this the control announces
   * nothing at all — describe what the code *does*, never its payload (which is a live one-time secret).
   */
  readonly ariaLabel = input<string>('QR-Code');

  /** The quiet zone the QR specification requires, in modules, on every side. */
  private static readonly QuietZone = 4;

  private readonly model = computed(() => {
    // Type version 0 = "pick the smallest version that fits". Error-correction level M (~15%) is the usual
    // trade for a code read off a screen: L scans no faster here and gives up the tolerance that matters when
    // the display has glare on it.
    const qr = qrcode(0, 'M');
    qr.addData(this.value());
    qr.make();
    return qr;
  });

  /** The code's edge length in modules, including the quiet zone. */
  protected readonly extent = computed(() => this.model().getModuleCount() + QrCodeComponent.QuietZone * 2);

  /** Every dark module as one SVG path, in module coordinates offset by the quiet zone. */
  protected readonly path = computed(() => {
    const qr = this.model();
    const count = qr.getModuleCount();
    const offset = QrCodeComponent.QuietZone;
    const parts: string[] = [];

    for (let row = 0; row < count; row++) {
      for (let col = 0; col < count; col++) {
        if (qr.isDark(row, col)) parts.push(`M${col + offset} ${row + offset}h1v1h-1z`);
      }
    }

    return parts.join('');
  });
}
